<template>
    <div class="product-creator flex justify-center items-center w-full max-w-screen-xl h-full mx-auto px-6 py-12">
        <div class="relative flex flex-col justify-center items-center w-full max-w-screen-xl h-full">

            <div v-if="uploading">
                <div class="absolute z-20 top-0 right-0 w-full h-full bg-white opacity-90"></div>
                <div class="absolute z-30 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div class="text-2xl font-bold text-center">
                        {{ __('home::orders.uploading_images') }}
                        ({{ uploadPercentage }}%)
                    </div>
                    <svg class="mt-12 h-12 w-12 animate-spin mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>
            </div>

            <div class="w-full sm:w-3/4 mx-auto">

                <PhotoEditor
                    v-if="photo"
                    :photo="photo"
                    :order="order"
                    class="w-full"
                    ref="photoEditor"
                    @saved="$emit('next')"
                />

                <div v-if="! photo">
                    <Menu as="div" class="relative">
                        <div>
                            <MenuButton
                                class="block relative top-0 left-0 h-auto w-24 h-24 bg-cover bg-center bg-no-repeat cursor-pointer text-gray-400 hover:text-gray-600 outline-none"
                            >
                                <div class="absolute z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                                    <span class="sr-only">Add photo</span>
                                    <PlusIcon class="w-14 h-14" />
                                </div>
                            </MenuButton>
                        </div>

                        <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
                            <MenuItems class="origin-center absolute left-1/2 -ml-6 top-20 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                                <div class="py-1">
                                    <MenuItem v-slot="{ active }">
                                        <button type="button" :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block w-full text-left px-4 py-2 text-sm']" @click="$refs.files.click()">
                                            {{ __('home::orders.upload_photo') }}
                                        </button>
                                    </MenuItem>
                                    <MenuItem v-slot="{ active }">
                                        <button type="button" :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block w-full text-left px-4 py-2 text-sm']" @click="stockPhotoSelectorOpen = true">
                                            {{ __('home::orders.use_stock_photo') }}
                                        </button>
                                    </MenuItem>
                                    <MenuItem v-slot="{ active }">
                                        <button type="button" :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block w-full text-left px-4 py-2 text-sm']" @click="socialPhotoSelectorOpen = true">
                                            {{ __('home::orders.use_social_photo') }}
                                        </button>
                                    </MenuItem>
                                </div>
                            </MenuItems>
                        </transition>
                    </Menu>

                    <input type="file" ref="files" accept="image/*" multiple class="hidden" @change="handleFileUploads()" />

                    <StockPhotoSelector
                        :open="stockPhotoSelectorOpen"
                        :order="order"
                        :product="selectedShape"
                        @close="stockPhotoSelectorOpen = false"
                        @append-photos="appendPhotos($event)"
                    />

                    <SocialPhotoSelector
                        :open="socialPhotoSelectorOpen"
                        :order="order"
                        :product="selectedShape"
                        @close="socialPhotoSelectorOpen = false"
                        @append-photos="appendPhotos($event)"
                    />

                </div>

            </div>

            <div class="w-full mt-16 bg-gray-50 rounded-md">

                <div class="flex justify-between items-center p-6">

                    <div>
                        {{ order.photos.length }}
                        {{ order.photos.length == 1 ? __('home::orders.photo').toLowerCase() : __('home::orders.photos').toLowerCase() }} <br>
                        {{ totalPrice }}
                    </div>

                    <Button type="button" @click="saveAndContinue()">
                        {{ __('home::orders.order_now') }}
                        <ChevronRightIcon class="h-4 w-4 ml-2" />
                    </Button>

                </div>
            </div>

        </div>
    </div>
</template>

<script>
    import axios from 'axios'
    import collect from 'collect.js'

    import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'

    import {
        XIcon,
    } from '@heroicons/vue/solid'

    import {
        ChevronRightIcon,
        PlusIcon,
    } from '@heroicons/vue/outline'

    export default {
        components: {
            Menu,
            MenuButton,
            MenuItem,
            MenuItems,
            PlusIcon,
            XIcon,
            ChevronRightIcon,
        },
        props: {
            photos: Object,
            order: Object,
            products: Object,
            totalPrice: String,
        },
        data() {
            return {
                uploading: false,
                uploadPercentage: 0,
                stockPhotoSelectorOpen: false,
                socialPhotoSelectorOpen: false,
                selectedShape: collect(this.products).firstWhere('id', this.order.meta.template),
                photo: null,
            }
        },
        mounted() {
            this.setPhoto();
        },
        methods: {
            setPhoto() {
                this.photo = collect(this.photos)
                    .firstWhere('product_id', this.selectedShape.id);
            },
            handleFileUploads() {

                var files = this.$refs.files.files;

                if (files.length == 0) {
                    return;
                }

                this.uploading = true;
                this.uploadPercentage = 0;

                var request = new FormData();
                request.append('product_id', this.selectedShape.id);
                request.append('files[0]', files[0]);

                axios
                    .post(this.$route('home::orders.photos.upload', { uuid: this.order.uuid }), request, {
                        header: {
                            'Content-Type': 'multipart/form-data'
                        },
                        onUploadProgress: function(progressEvent) {
                            this.uploadPercentage = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
                        }.bind(this),
                    })
                    .then((res) => {
                        this.uploading = false;
                        this.appendPhotos(res.data);
                    });
            },
            appendPhotos(newPhotos) {
                var photos = this.photos;
                newPhotos.forEach(newPhoto => photos.push(newPhoto));
                this.$emit('update:photos', photos);

                this.$gtm.trackEvent({
                    event: 'UploadPhoto',
                    category: 'PhotoCreation',
                });

                this.setPhoto();
            },
            updatePhoto(photo) {
                this.editing = photo;
                var photos = this.photos;
                var index = this.photos.findIndex(x => x.id === photo.id);
                photos.splice(index, 1, photo);
                this.$emit('update:photos', photos);
            },
            deletePhoto() {
                axios
                    .delete(this.$route('home::orders.photos.destroy', { uuid: this.order.uuid, photo: this.editing.id }))
                    .then((res) => {
                        var photos = this.photos;
                        var index = this.photos.findIndex(x => x.id === this.editing.id);
                        photos.splice(index, 1);
                        this.$emit('update:photos', photos);
                        this.editing = null;
                    });
            },
            saveAndContinue() {
                this.$refs.photoEditor.save();
            },
        },
    }
</script>
